import { api } from '@shared/web/api-client';
import type {
  CreatorBooking,
  CreatorContractsPayload,
  CreatorIdentity,
  CreatorPaymentsPayload,
  CreatorPayoutPayload,
  CreatorProductId,
  CreatorProductsPayload,
  CreatorSessionPayload,
  CreatorSignedContract,
  CreatorSignUpInfo,
  CreatorSlotsPayload,
  CreatorTicket,
  CreatorTicketsPayload,
  CreatorVideosPayload,
  SupportAvailability,
  NewCreatorVideo,
  OnboardingVideo,
  NewSupportTicket,
  PayoutInput,
  SignerContract,
} from '@shared/web/types';

/**
 * Every call the creator app makes.
 *
 * One module rather than one per tab: the surface is small, and a creator's
 * session spans all of it. Split by tab, the shared auth calls end up
 * duplicated or in a fifth file that the other four import anyway.
 */
export const creatorApi = {
  // ── Auth ─────────────────────────────────────────────────────────────────
  /** What the screen behind a signing link needs to know before it renders. */
  signUpInfo: (token: string) =>
    api.get<CreatorSignUpInfo>(`/api/creator/auth/signup/${encodeURIComponent(token)}`),

  signUp: (input: {
    token: string;
    password: string;
    confirmPassword: string;
    name?: string;
  }) => api.post<CreatorSessionPayload & { contractId: string }>('/api/creator/auth/signup', input),

  logIn: (email: string, password: string) =>
    api.post<CreatorSessionPayload>('/api/creator/auth/login', { email, password }),

  me: () => api.get<CreatorSessionPayload>('/api/creator/auth/me'),
  logOut: () => api.post<{ ok: true }>('/api/creator/auth/logout'),

  requestPasswordReset: (email: string) =>
    api.post<{ ok: true }>('/api/creator/auth/password/request-reset', { email }),
  verifyPasswordResetCode: (email: string, code: string) =>
    api.post<{ ok: true }>('/api/creator/auth/password/verify-code', { email, code }),
  resetPassword: (input: {
    email: string;
    code: string;
    password: string;
    confirmPassword: string;
  }) => api.post<{ ok: true }>('/api/creator/auth/password/reset', input),
  verifyActivationCode: (email: string, code: string) =>
    api.post<{ ok: true }>('/api/creator/auth/activate/verify-code', { email, code }),
  activate: (input: { email: string; code: string; password: string; confirmPassword: string }) =>
    api.post<{ ok: true }>('/api/creator/auth/activate', input),

  changePassword: (input: {
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }) => api.patch<{ ok: true }>('/api/creator/auth/password', input),

  // ── Onboarding ───────────────────────────────────────────────────────────
  onboardingVideo: () => api.get<OnboardingVideo>('/api/creator/onboarding/video'),
  markVideoWatched: (percent: number) =>
    api.post<{ ok: true; watchedAt: string }>('/api/creator/onboarding/video/watched', {
      percent,
    }),

  slots: () => api.get<CreatorSlotsPayload>('/api/creator/onboarding/slots'),
  booking: () => api.get<{ booking: CreatorBooking | null }>('/api/creator/onboarding/booking'),
  book: (input: { slotId: string; timezone?: string; note?: string }) =>
    api.post<{ booking: CreatorBooking }>('/api/creator/onboarding/booking', input),

  // ── Videos ───────────────────────────────────────────────────────────────
  videos: () => api.get<CreatorVideosPayload>('/api/creator/videos'),
  addVideo: (input: NewCreatorVideo) =>
    api.post<CreatorVideosPayload>('/api/creator/videos', input),

  // ── Payments ─────────────────────────────────────────────────────────────
  payments: () => api.get<CreatorPaymentsPayload>('/api/creator/payments'),
  payout: () => api.get<CreatorPayoutPayload>('/api/creator/payout'),
  savePayout: (input: PayoutInput) => api.put<CreatorPayoutPayload>('/api/creator/payout', input),
  confirmPayout: () => api.post<CreatorPayoutPayload>('/api/creator/payout/confirm'),

  // ── Contracts ────────────────────────────────────────────────────────────
  contracts: () => api.get<CreatorContractsPayload>('/api/creator/contracts'),
  /**
   * One SIGNED contract, in full.
   *
   * A different shape from the list's `CreatorContract`: this one carries the
   * document body and its hash, which the list deliberately does not — sending
   * every contract's full text to render a four-row list would be wasteful and
   * would put signed agreements in memory nothing on that screen reads.
   */
  contract: (id: string) =>
    api.get<{ contract: CreatorSignedContract }>(`/api/creator/contracts/${id}`),
  /**
   * A full navigation rather than a fetch: the response is an attachment, and
   * letting the browser handle it gives the native "save file" behaviour
   * instead of us reconstructing it from a blob.
   */
  downloadContractUrl: (id: string) => `/api/creator/contracts/${id}/download`,

  /**
   * Signing, addressed by contract ID.
   *
   * Not by the emailed token: it is stored as a SHA-256, so no screen can
   * rebuild a link from it. Inside the portal the session already says who the
   * creator is, which makes the id sufficient.
   */
  contractToSign: (id: string) =>
    api.get<{ contract: SignerContract }>(`/api/creator/contracts/${id}/sign`),

  requestSigningCode: (id: string) =>
    api.post<{ ok: true; sentTo: string }>(`/api/creator/contracts/${id}/code`),

  signContract: (id: string, input: { code: string; signedName: string; agreed: true }) =>
    api.post<{ contract: SignerContract }>(`/api/creator/contracts/${id}/sign`, input),

  declineContract: (id: string, reason?: string) =>
    api.post<{ contract: SignerContract }>(`/api/creator/contracts/${id}/decline`, { reason }),

  // ── Products ─────────────────────────────────────────────────────────────
  products: () => api.get<CreatorProductsPayload>('/api/creator/products'),
  provisionRoxy: () =>
    api.post<CreatorProductsPayload>('/api/creator/products/roxy/provision'),
  provisionSunday: () =>
    api.post<CreatorProductsPayload>('/api/creator/products/sunday/provision'),
  requestProduct: (product: Exclude<CreatorProductId, 'roxy' | 'sunday'>) =>
    api.post<CreatorProductsPayload>(`/api/creator/products/${product}/request`),

  // ── Support ──────────────────────────────────────────────────────────────
  supportAvailability: () => api.get<SupportAvailability>('/api/creator/support/availability'),
  tickets: () => api.get<CreatorTicketsPayload>('/api/creator/support'),
  ticket: (id: string) => api.get<{ ticket: CreatorTicket }>(`/api/creator/support/${id}`),
  openTicket: (input: NewSupportTicket) =>
    api.post<{ ticket: CreatorTicket }>('/api/creator/support', input),
  replyToTicket: (id: string, body: string) =>
    api.post<{ ticket: CreatorTicket }>(`/api/creator/support/${id}/reply`, { body }),
  resolveTicket: (id: string) =>
    api.post<{ ticket: CreatorTicket }>(`/api/creator/support/${id}/resolve`),
  rateTicket: (id: string, rating: 'up' | 'down') =>
    api.post<{ ticket: CreatorTicket }>(`/api/creator/support/${id}/feedback`, { rating }),
  addTicketFeedback: (id: string, comment: string) =>
    api.post<{ ticket: CreatorTicket }>(`/api/creator/support/${id}/feedback/comment`, { comment }),
  acknowledgeAnswer: (id: string, pendingAnswerAt: string) =>
    api.post(`/api/creator/support/${id}/seen`, { pendingAnswerAt }),
};

export type { CreatorIdentity };
